import React, { useState, useEffect, useCallback } from 'react';
import { Play, Timer, Trophy, RefreshCw, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Game Constants
const GAME_DURATION = 60; // seconds
const NUMBER_RANGE = { min: 1, max: 100 };

// Rule Definitions
const RULES = [
  { id: 'even', label: 'Even Numbers', check: (n) => n % 2 === 0 },
  { id: 'odd', label: 'Odd Numbers', check: (n) => n % 2 !== 0 },
  { id: 'mult3', label: 'Multiples of 3', check: (n) => n % 3 === 0 },
  { id: 'mult4', label: 'Multiples of 4', check: (n) => n % 4 === 0 },
  { id: 'mult5', label: 'Multiples of 5', check: (n) => n % 5 === 0 },
  { id: 'gt50', label: 'Greater than 50', check: (n) => n > 50 },
  { id: 'lt50', label: 'Less than 50', check: (n) => n < 50 },
];

const VennBlitz = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameover
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [ruleA, setRuleA] = useState(null);
  const [ruleB, setRuleB] = useState(null);
  const [feedback, setFeedback] = useState(null); // { type: 'correct' | 'wrong', message: string }

  // Timer Logic
  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && gameState === 'playing') {
      setGameState('gameover');
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  // Game Logic
  const startGame = () => {
    // Pick two distinct rules
    const r1 = RULES[Math.floor(Math.random() * RULES.length)];
    let r2 = RULES[Math.floor(Math.random() * RULES.length)];
    while (r2.id === r1.id || isIncompatible(r1, r2)) {
      r2 = RULES[Math.floor(Math.random() * RULES.length)];
    }
    
    setRuleA(r1);
    setRuleB(r2);
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setGameState('playing');
    setFeedback(null);
    generateNumber();
  };

  // Prevent impossible combinations (though most work, some might be boring like Even + Odd = Empty Intersection)
  const isIncompatible = (r1, r2) => {
      // Prevent Even + Odd (Intersection is explicitly empty, which is valid but maybe confusing for users expecting overlap)
      if ((r1.id === 'even' && r2.id === 'odd') || (r1.id === 'odd' && r2.id === 'even')) return false; 
      // Actually, disjoint sets are fine! It tests understanding of empty sets.
      // But let's prevent identical logical rules if we had synonyms.
      // Let's prevent gt50 + lt50 (Partition, also fine).
      // Let's just ensue they are distinct objects.
      return false;
  };

  const generateNumber = () => {
    const n = Math.floor(Math.random() * (NUMBER_RANGE.max - NUMBER_RANGE.min + 1)) + NUMBER_RANGE.min;
    setCurrentNumber(n);
  };

  const handleZoneClick = (zone) => {
    if (gameState !== 'playing') return;

    const matchesA = ruleA.check(currentNumber);
    const matchesB = ruleB.check(currentNumber);

    let correctZone = '';
    if (matchesA && matchesB) correctZone = 'both';
    else if (matchesA && !matchesB) correctZone = 'a_only';
    else if (!matchesA && matchesB) correctZone = 'b_only';
    else correctZone = 'neither';

    if (zone === correctZone) {
      // Correct!
      setScore(s => s + 10);
      setFeedback({ type: 'correct', message: '+10' });
      generateNumber();
    } else {
      // Wrong!
      setScore(s => Math.max(0, s - 5));
      setFeedback({ type: 'wrong', message: 'Wrong Region!' });
    }

    // Clear feedback after short delay
    setTimeout(() => setFeedback(null), 800);
  };

  // ...
  
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center p-4 font-sans">
      {/* Header */}
      <div className="w-full max-w-4xl flex items-center justify-between mb-8">
         <div className="flex items-center gap-4">
             {/* Back Button */}
             <button
                onClick={() => navigate('/games')}
                className="flex items-center text-blue-600 hover:text-blue-800 font-bold"
             >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back to Games
             </button>
             <div>
                <h1 className="text-3xl font-bold text-slate-800">Venn Blitz</h1>
                <p className="text-slate-500">Sort the numbers into the correct sets!</p>
             </div>
         </div>
         {/* ... */}
        <div className="flex gap-6 text-xl font-bold">
            <div className={`flex items-center gap-2 ${timeLeft < 10 ? 'text-red-500 animate-pulse' : 'text-slate-700'}`}>
                <Timer /> {timeLeft}s
            </div>
            <div className="flex items-center gap-2 text-blue-600">
                <Trophy /> {score}
            </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="relative w-full max-w-4xl flex-grow flex flex-col items-center justify-center">
        
        {/* Current Number Card */}
        {gameState === 'playing' && (
             <div className="relative z-20 flex flex-col items-center animate-bounce-slight mb-4">
                <div className="bg-white text-slate-900 border-4 border-slate-800 rounded-full w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center text-3xl sm:text-4xl font-extrabold shadow-2xl">
                    {currentNumber}
                </div>
                {feedback && (
                    <div className={`mt-2 font-bold text-lg ${feedback.type === 'correct' ? 'text-green-600' : 'text-red-500'}`}>
                        {feedback.message}
                    </div>
                )}
             </div>
        )}

        {/* Venn Diagram Visuals - CSS Art */}
        {gameState === 'playing' && ruleA && ruleB && (
            <>
            {/* Rule Labels - Placed above diagram to avoid overlap */}
            <div className="w-full max-w-[600px] flex justify-between px-2 mb-4 gap-2 z-10">
                <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg text-sm sm:text-base font-bold border-2 border-blue-200 text-center flex-1 max-w-[45%]">
                    Set A: {ruleA.label}
                </div>
                <div className="bg-red-100 text-red-800 px-3 py-2 rounded-lg text-sm sm:text-base font-bold border-2 border-red-200 text-center flex-1 max-w-[45%]">
                    Set B: {ruleB.label}
                </div>
            </div>

            <div className="relative w-full max-w-[600px] aspect-[3/2] mx-auto">

                {/* Left Circle (Set A) - Clickable Area A Only */}
                <div 
                    onClick={() => handleZoneClick('a_only')}
                    className="absolute left-0 top-0 h-full aspect-square rounded-full border-4 border-blue-500 bg-blue-500/10 hover:bg-blue-500/20 cursor-pointer transition-colors z-10 flex items-center justify-start pl-[10%]"
                >
                    <span className="opacity-0 hover:opacity-100 font-bold text-blue-700 select-none">A Only</span>
                </div>

                {/* Right Circle (Set B) - Clickable Area B Only */}
                <div 
                    onClick={() => handleZoneClick('b_only')}
                    className="absolute right-0 top-0 h-full aspect-square rounded-full border-4 border-red-500 bg-red-500/10 hover:bg-red-500/20 cursor-pointer transition-colors z-10 flex items-center justify-end pr-[10%]"
                >
                    <span className="opacity-0 hover:opacity-100 font-bold text-red-700 select-none">B Only</span>
                </div>

                {/* Intersection - Clickable Area Both */}
                {/* 
                   Math for Intersection Position:
                   Container Aspect Ratio: 3/2 (e.g., 600w x 400h)
                   Circles are square, height = 100% of container height.
                   Circle width = 400px relative to 600px container = 66.6%.
                   Left Circle: left: 0
                   Right Circle: right: 0 (which means left = 100% - 66.6% = 33.3%)
                   
                   Overlap starts at Right Circle Left Edge (33.3%) and ends at Left Circle Right Edge (66.6%).
                   So Intersection Width = 66.6% - 33.3% = 33.3% of Container Width.
                   
                   Generous Interaction zone:
                   Left: 30%
                   Width: 40%
                   Top: 20%
                   Height: 60%
                */}
                <div 
                    onClick={() => handleZoneClick('both')}
                    className="absolute left-[30%] top-[20%] w-[40%] h-[60%] rounded-full hover:bg-purple-500/30 cursor-pointer z-20 flex items-center justify-center"
                    style={{ 
                        borderRadius: '50%'
                    }}
                >
                    <span className="opacity-0 hover:opacity-100 font-bold text-purple-800 select-none">Both</span>
                </div>

                {/* Outside - Neither Button (Separate UI for clarity) */}
                
            </div>
            </>
        )}

        {/* Neither Button */}
        {gameState === 'playing' && (
            <button 
                onClick={() => handleZoneClick('neither')}
                className="mt-8 px-8 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition border-2 border-slate-300"
            >
                Neither (Outside)
            </button>
        )}

        {/* Start / Game Over Screens */}
        {gameState === 'menu' && (
             <div className="absolute inset-0 bg-white/90 z-30 flex flex-col items-center justify-center rounded-2xl">
                 <h2 className="text-5xl font-bold text-slate-800 mb-6">Venn Blitz</h2>
                 <p className="text-xl text-slate-600 mb-8 max-w-md text-center">
                     Rules will appear. A number will drop. <br/>
                     Sort it into the correct region of the diagram before time runs out!
                 </p>
                 <button onClick={startGame} className="px-12 py-5 bg-blue-600 text-white rounded-xl font-bold text-2xl hover:bg-blue-500 transition shadow-lg hover:scale-105 flex items-center gap-3">
                     <Play size={32} /> Start Game
                 </button>
             </div>
        )}

        {gameState === 'gameover' && (
             <div className="absolute inset-0 bg-white/95 z-30 flex flex-col items-center justify-center rounded-2xl">
                 <h2 className="text-4xl font-bold text-slate-800 mb-4">Time's Up!</h2>
                 <div className="text-6xl font-extrabold text-blue-600 mb-2">{score}</div>
                 <p className="text-slate-500 mb-8">Final Score</p>
                 <button onClick={startGame} className="px-10 py-4 bg-green-600 text-white rounded-xl font-bold text-xl hover:bg-green-500 transition shadow-lg flex items-center gap-2">
                     <RefreshCw /> Play Again
                 </button>
             </div>
        )}

      </div>

      {/* CSS for tricky shapes if needed, mostly simplified above */}
      <style jsx>{`
        .animate-bounce-slight {
            animation: bounce 1s infinite;
        }
        @keyframes bounce {
            0%, 100% { transform: translateY(-5%); }
            50% { transform: translateY(5%); }
        }
      `}</style>
    </div>
  );
};

export default VennBlitz;
